// ============================================================================
// useQuickSearchForAppsLibrary — Fast semantic search for Apps Library
//
// Drop-in replacement for `useRepositorySearch` that uses the synchronous
// `quickSearch` vector-similarity mutation instead of the slow agent-based
// `searchRepository` → `onAgentResponse` subscription pipeline.
//
// Key differences from useRepositorySearch:
//   - Single synchronous GraphQL mutation (~1–2s) vs multi-second agent flow
//   - Returns ordered app_id strings extracted from quickSearch results
//   - Same public interface: { query, resultAppIds, isSearching, searchError,
//     runSearch, resetSearch } — Apps.jsx hydration logic works unchanged
//   - 10s hard timeout with AbortController (auth pre-warm excluded)
//   - Monotonic request ID for staleness protection
//   - Feature-switch gated (APPS_LIBRARY_REPO_SEARCH_ENABLED)
//
// The hook returns `resultAppIds` in relevance order (by vector similarity
// score). Apps.jsx hydrates these IDs against allAppsRef.current exactly as
// before, preserving all existing filter/display/favorites/ratings logic.
// ============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { quickSearch } from 'src/utilities/AgentChatAPIs';
import { prewarmAuthToken } from 'src/utilities/AISearchAppSync';
import { createDebugLog } from 'src/Ateam-BI/AutoMate/utils/aiDebugLog';
import { APPS_LIBRARY_REPO_SEARCH_ENABLED } from 'src/Ateam-BI/AutoMate/constants';
import { captureMetrics, createKatalTimerStopWatch, stopAndCaptureTimeMetricsWithBasicAttributes } from 'src/katalMetrics/metricsHelperMethods';
import { ACTION_METHODS, METRIC_TYPE, SITE_SERVICES } from 'src/katalMetrics/metricsModels';

const debugLog = createDebugLog('[useQuickSearchForAppsLibrary]');

/** Hard timeout for the quickSearch round-trip (auth pre-warm excluded). */
const QUICK_SEARCH_TIMEOUT_MS = 10000;

/** Maximum results to request from the vector search backend. */
const MAX_RESULTS = 100;

/**
 * Fast semantic search hook for Apps Library.
 *
 * Exposes the same public interface as `useRepositorySearch` so Apps.jsx can
 * swap with minimal changes:
 *   - query: the active search query string
 *   - resultAppIds: ordered app_id strings from the latest successful search
 *   - isSearching: true while a search is in flight
 *   - searchError: safe error message string, or null
 *   - runSearch: (rawQuery: string) => void — fire a new quick search
 *   - resetSearch: () => void — clear state without a network call
 *
 * @param {string} userId - User alias for metrics
 * @param {string} [pagePath='/AppsLibrary'] - Page path for metrics
 */
const useQuickSearchForAppsLibrary = (userId, pagePath = '/AppsLibrary') => {
  const [query, setQuery] = useState('');
  const [resultAppIds, setResultAppIds] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Monotonic request ID for staleness guards.
  const requestIdRef = useRef(0);
  // Whether the component is still mounted.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /**
   * Fire a new quick search for the given query.
   * Rejects blank input without a network call.
   * When the feature switch is disabled, acts as a no-op.
   */
  const runSearch = useCallback(async (rawQuery) => {
    const trimmed = (rawQuery ?? '').trim();
    if (!trimmed) return;
    if (!APPS_LIBRARY_REPO_SEARCH_ENABLED) {
      debugLog('runSearch suppressed — APPS_LIBRARY_REPO_SEARCH_ENABLED is false');
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    debugLog('new search', { query: trimmed, requestId: currentRequestId });

    // Reset state for the new search.
    setQuery(trimmed);
    setSearchError(null);
    setIsSearching(true);

    // --- Metrics: emit submit counter ---
    captureMetrics(
      ACTION_METHODS.REPOSITORY_SEARCH_SUBMIT,
      [{ metricName: 'repositorySearchSubmit', metricValue: 1, metricType: METRIC_TYPE.COUNTER, isMonitor: true }],
      SITE_SERVICES.APPS_LIBRARY
    );
    const searchTimer = createKatalTimerStopWatch('repositorySearchElapsed');

    // Pre-warm auth token outside the timeout window.
    try {
      await prewarmAuthToken();
    } catch (_) {
      // Non-fatal — quickSearch will retry token if needed.
    }

    // Staleness check after async auth pre-warm.
    if (currentRequestId !== requestIdRef.current || !mountedRef.current) return;

    // Execute quickSearch with hard timeout.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), QUICK_SEARCH_TIMEOUT_MS);

    try {
      const result = await quickSearch(trimmed, MAX_RESULTS, { signal: controller.signal });
      clearTimeout(timeout);

      // Staleness guard.
      if (currentRequestId !== requestIdRef.current || !mountedRef.current) return;

      const payload = result?.data?.quickSearch;
      const results = payload?.results || [];

      // Extract ordered app_ids from results (same shape Apps.jsx expects).
      const ids = results
        .map(r => r?.parsed?.app_id)
        .filter(Boolean);

      debugLog('search complete', { resultCount: ids.length, requestId: currentRequestId });

      setResultAppIds(ids);
      setIsSearching(false);

      // --- Metrics: emit terminal metric ---
      const actionMethod = ids.length > 0
        ? ACTION_METHODS.REPOSITORY_SEARCH_SUCCESS
        : ACTION_METHODS.REPOSITORY_SEARCH_EMPTY;
      captureMetrics(
        actionMethod,
        [
          { metricName: 'repositorySearchResult', metricValue: 1, metricType: METRIC_TYPE.COUNTER, isMonitor: true },
          { metricName: 'resultCount', metricValue: ids.length, metricType: METRIC_TYPE.COUNTER, isMonitor: true },
        ],
        SITE_SERVICES.APPS_LIBRARY
      );
      if (!searchTimer.isStopped) {
        stopAndCaptureTimeMetricsWithBasicAttributes(searchTimer, userId, pagePath, actionMethod, SITE_SERVICES.APPS_LIBRARY);
      }
    } catch (error) {
      clearTimeout(timeout);

      // Staleness guard.
      if (currentRequestId !== requestIdRef.current || !mountedRef.current) return;

      if (error?.name === 'AbortError') {
        console.warn(`[QuickSearchAppsLibrary] timed out after ${QUICK_SEARCH_TIMEOUT_MS}ms`);
        setSearchError('Search timed out. Please try again.');
      } else {
        console.warn('[QuickSearchAppsLibrary] failed:', error?.message || 'unknown');
        setSearchError('Search failed. Please try again.');
      }
      setIsSearching(false);

      // --- Metrics: emit failure ---
      captureMetrics(
        ACTION_METHODS.REPOSITORY_SEARCH_FAILURE,
        [
          { metricName: 'repositorySearchResult', metricValue: 1, metricType: METRIC_TYPE.COUNTER, isMonitor: true },
          { metricName: 'resultCount', metricValue: 0, metricType: METRIC_TYPE.COUNTER, isMonitor: true },
        ],
        SITE_SERVICES.APPS_LIBRARY
      );
      if (!searchTimer.isStopped) {
        stopAndCaptureTimeMetricsWithBasicAttributes(searchTimer, userId, pagePath, ACTION_METHODS.REPOSITORY_SEARCH_FAILURE, SITE_SERVICES.APPS_LIBRARY);
      }
    }
  }, [userId, pagePath]);

  /**
   * Clear search state without making a network call.
   * Invalidates any in-flight request.
   */
  const resetSearch = useCallback(() => {
    requestIdRef.current++;
    debugLog('reset', { requestId: requestIdRef.current });
    setQuery('');
    setResultAppIds([]);
    setIsSearching(false);
    setSearchError(null);
  }, []);

  return {
    query,
    resultAppIds,
    isSearching,
    searchError,
    runSearch,
    resetSearch,
  };
};

export default useQuickSearchForAppsLibrary;
